import { useEffect, useState } from "react";
import { RefreshCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMe, useConfig } from "@/lib/queries";
import { openSettings, useUi } from "@/lib/store";
import { toast } from "sonner";
import { Shell } from "@/components/shell/Shell";
import { openFromUrl } from "@/features/assistant/openLink";
import { ConnectPage } from "@/features/settings/ConnectPage";
import { RefManDonePage } from "@/features/refmanagers/RefManDonePage";
import { ALWAYS_PUBLIC, PublicSite, SIGNED_OUT_ONLY } from "@/features/public/PublicSite";
import { usePathname } from "@/features/public/PublicLayout";
import { rememberOfferFromUrl } from "@/features/settings/offer";
import { rememberInviteFromUrl } from "@/features/public/invite";

rememberOfferFromUrl();
rememberInviteFromUrl();

export function App() {
  const config = useConfig();
  const me = useMe();
  const theme = useUi((s) => s.theme);
  const setUser = useUi((s) => s.setUser);
  const path = usePathname();

  useEffect(() => {
    if (!me.data) return;
    const url = new URL(window.location.href);
    const result = url.searchParams.get("chatgpt");
    if (result !== "linked" && result !== "failed") return;
    url.searchParams.delete("chatgpt");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    openSettings({ section: "ai-provider", ...(result === "linked" ? { provider: "chatgpt" } : {}) });
    if (result === "linked") toast.success("ChatGPT connected. Choose a model and save to use your subscription.");
    else toast.error("ChatGPT could not be linked. Try again and allow ChatGPT plan usage. Your AI service has not changed.");
  }, [me.data?.id]);

  useEffect(() => {
    const apply = () => {
      const dark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
      document.documentElement.classList.toggle("dark", dark);
    };
    apply();
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [theme]);

  useEffect(() => {
    setUser(me.data?.id ?? null);
  }, [me.data?.id, setUser]);

  // Public pages keep their own title (PublicSite).
  const inApp = !!me.data && !ALWAYS_PUBLIC.has(path);
  useEffect(() => {
    if (inApp && config.data?.appName) document.title = config.data.appName;
  }, [inApp, config.data?.appName]);

  if (config.isLoading || me.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground" role="status">
        Loading…
      </div>
    );
  }
  if (config.isError || me.isError) return <Unreachable retry={() => Promise.all([config.refetch(), me.refetch()])} />;
  if (ALWAYS_PUBLIC.has(path)) return <PublicSite path={path} signedIn={!!me.data} />;
  if (!me.data) {
    if (path === "/" || SIGNED_OUT_ONLY.has(path)) return <PublicSite path={path} signedIn={false} />;
    // Any other page (e.g. an AI assistant asking for access) needs signing in first, then comes back.
    window.location.replace(`/login?next=${encodeURIComponent(path + window.location.search)}`);
    return null;
  }
  if (SIGNED_OUT_ONLY.has(path)) {
    window.location.replace("/");
    return null;
  }
  // An AI agent asking for access (OAuth consent) lands here after signing in.
  if (window.location.pathname === "/connect") return <ConnectPage />;
  // Back from signing in to Zotero or Mendeley.
  if (window.location.pathname === "/refman/done") return <RefManDonePage />;
  // A link from an email or Telegram message opens what it points to.
  openFromUrl();
  return <Shell user={me.data} />;
}

/**
 * BrainLog could not be reached while opening: offline, or the server is restarting (an update
 * takes under a minute). It keeps trying, and opens by itself once the server answers.
 */
function Unreachable({ retry }: { retry(): Promise<unknown> }) {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [wait, setWait] = useState(5);
  const [busy, setBusy] = useState(false);
  const again = async () => {
    setBusy(true);
    try {
      await retry();
    } finally {
      setBusy(false);
      setWait(10);
    }
  };
  useEffect(() => {
    const on = () => {
      setOnline(navigator.onLine);
      if (navigator.onLine) void again();
    };
    window.addEventListener("online", on);
    window.addEventListener("offline", on);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", on);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!online || busy) return;
    if (wait <= 0) return void again();
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait, online, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex h-full items-center justify-center p-6 text-center" role="alert">
      <div className="max-w-sm space-y-3">
        <img src="/icon-192.png" alt="" aria-hidden className="mx-auto size-12" />
        {online ? (
          <>
            <p className="font-medium">BrainLog can't be reached right now.</p>
            <p className="text-sm text-muted-foreground">It may be installing an update, which takes less than a minute. Your notes are safe. This page opens by itself as soon as BrainLog is back.</p>
          </>
        ) : (
          <>
            <p className="flex items-center justify-center gap-2 font-medium">
              <WifiOff className="size-4" aria-hidden /> You are offline.
            </p>
            <p className="text-sm text-muted-foreground">Check your internet connection. BrainLog opens by itself when you are back online.</p>
          </>
        )}
        <Button size="sm" variant="outline" disabled={busy || !online} onClick={() => void again()}>
          <RefreshCw className={busy ? "animate-spin" : undefined} /> {busy ? "Trying…" : online ? `Try again now (automatically in ${wait} s)` : "Waiting for a connection…"}
        </Button>
        {import.meta.env.DEV && <p className="text-xs text-muted-foreground">Development: start the server with pnpm dev.</p>}
      </div>
    </div>
  );
}
